import Uppy from '@uppy/core'
import Dashboard from '@uppy/dashboard'
import AwsS3Multipart from '@uppy/aws-s3-multipart'
import Polish from '@uppy/locales/lib/pl_PL'

import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'

// Structured after uppy_comment_file_upload.js — see that file for the full
// rationale behind the single always-on MutationObserver, the
// isBeingDestroyed teardown guard, and live (not cached-at-init) DOM lookups
// for the submit button / CSRF token. recommendations/new.html.erb is only
// ever reached via a full Turbolinks navigation (never an AJAX-loaded modal),
// but the same full-page-load CSRF-rewrite pitfall applies, so the live
// lookups are kept; the modal-reopen race the MutationObserver singleton
// guards against can't currently happen here, but mirroring the same
// structure keeps this pack consistent with its sibling and safe if that
// ever changes.
let uppyInstance;
let trackedDragDropArea;
let isBeingDestroyed = false;

function destroyUppyInstance() {
    if (uppyInstance) {
        isBeingDestroyed = true;
        uppyInstance.destroy();
        isBeingDestroyed = false;
        uppyInstance = null;
    }
    trackedDragDropArea = null;
}

function handleDomMutation() {
    if (trackedDragDropArea && !document.body.contains(trackedDragDropArea)) {
        destroyUppyInstance();
    }
    initRecommendationFileUppyIfPresent();
}

function initRecommendationFileUppyIfPresent() {
    const dragDropArea = document.querySelector('#recommendation-file-uppy-drag-drop-area')

    if (!dragDropArea || dragDropArea.dataset.uppyInitialized === 'true') return;
    dragDropArea.dataset.uppyInitialized = 'true';
    trackedDragDropArea = dragDropArea;

    // The drag-drop area and the @recommendation form are siblings inside the
    // same .user-show wrapper (recommendations/new.html.erb) — unlike the
    // CommentFile widget, there's only one <form> under that wrapper here, so
    // no textarea-based filtering is needed to disambiguate it.
    function getRecommendationForm() {
        return $(dragDropArea).closest('.user-show').find('form').first();
    }

    function getSubmitButtons() {
        return getRecommendationForm().find("input[type='submit']");
    }

    // form_for(@recommendation) adds its own authenticity_token hidden field,
    // and recommendations/_form.html.erb adds a second explicit one further
    // down (rendered with the same token this widget's data-token was
    // rendered with) — rails-ujs's refreshCSRFTokens rewrites every
    // input[name="authenticity_token"] in the DOM on load, so read the form's
    // own (last) field live, at upload time, rather than relying on the
    // snapshot baked into data-token at render time.
    function getCurrentAuthenticityToken() {
        const field = getRecommendationForm().find('input[name="authenticity_token"]').last();
        return field.length ? field.val() : dragDropArea.dataset.token;
    }

    // Pages with multiple recommendations-table submit buttons (see
    // recommendations/_form.html.erb) render each with its own distinct
    // label. jQuery's .val() getter only ever reads the *first* element in a
    // multi-element selection, so capturing one shared "original label" via
    // submitButtons.val() and writing it back to every button on completion
    // would collapse all of them to the first button's text. Storing each
    // button's own label on itself avoids that.
    function rememberOriginalLabels(submitButtons) {
        submitButtons.each(function () {
            if (this.dataset.originalSubmitLabel === undefined) {
                this.dataset.originalSubmitLabel = this.value;
            }
        });
    }

    function restoreOriginalLabels(submitButtons) {
        submitButtons.each(function () {
            if (this.dataset.originalSubmitLabel !== undefined) {
                this.value = this.dataset.originalSubmitLabel;
            }
        });
    }

    dragDropArea.style.width = `calc(100% - 20px)`;

    uppyInstance = new Uppy({
        autoProceed: true,
        restrictions: {
            maxFileSize: parseInt(dragDropArea.dataset.maxFileSize),
            maxNumberOfFiles: parseInt(dragDropArea.dataset.maxNumberOfFiles)
        },
        locale: Polish
    })

    uppyInstance.use(Dashboard, {
        inline: true,
        target: '#recommendation-file-uppy-drag-drop-area',
        showProgressDetails: true,
        proudlyDisplayPoweredByUppy: false,
        height: 300,
        width: '100%',
        showRemoveButtonAfterComplete: true
    })

    uppyInstance.use(AwsS3Multipart, {
        id: 'AwsS3Multipart',

        mode: 'custom',

        shouldUseMultipart: (_file) => true,

        createMultipartUpload: async (file) => {
            const response = await fetch('/s3/multipart', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                },
                body: JSON.stringify({
                    filename: file.name,
                    type: file.type
                })
            })
            const result = await response.json()
            return {
                uploadId: result.uploadId,
                key: result.key
            }
        },

        signPart: async (file, {uploadId, key, partNumber}) => {
            const res = await fetch(`/s3/multipart/${uploadId}/${partNumber}?key=${encodeURIComponent(key)}`, {
                method: 'GET'
            })

            const {url} = await res.json()
            return {
                url,
                headers: {'Content-Type': 'application/octet-stream'}
            }
        },

        completeMultipartUpload: async (file, {key, uploadId, parts}) => {
            const response = await fetch(`/s3/multipart/${uploadId}/complete?key=${encodeURIComponent(key)}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                },
                body: JSON.stringify({
                    parts,
                    model: 'RecommendationFile',
                    token: getCurrentAuthenticityToken()
                })
            })
            const result = await response.json()

            // The file itself is already fully uploaded to S3 at this point —
            // this request only asks the server to persist the
            // RecommendationFile record (running e.g. extension validation).
            // A 4xx here means that failed, so surface it instead of
            // reporting success with no record ever created.
            if (!response.ok) {
                throw new Error((result.errors || []).join(', ') || 'Nie udało się zapisać pliku')
            }

            uppyInstance.setFileState(file.id, {modelId: result.document_file_id})

            return {location: result.location}
        },

        abortMultipartUpload: async (file, {key, uploadId}) => {
            await fetch(`/s3/multipart/${uploadId}?key=${encodeURIComponent(key)}`, {
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                }
            })
        }
    })

    uppyInstance.on('file-added', () => {
        const submitButtons = getSubmitButtons();
        rememberOriginalLabels(submitButtons);
        submitButtons.prop('disabled', true).val('Trwa dodawanie plików');
    });

    uppyInstance.on('complete', () => {
        const submitButtons = getSubmitButtons();
        submitButtons.prop('disabled', false);
        restoreOriginalLabels(submitButtons);
    });

    uppyInstance.on('file-removed', (file) => {
        if (isBeingDestroyed) return;

        const submitButtons = getSubmitButtons();
        submitButtons.prop('disabled', false);
        restoreOriginalLabels(submitButtons);

        if (file.modelId) {
            fetch('/recommendation_files/' + file.modelId, {
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                }
            })
        }
    })
}

document.addEventListener('turbolinks:before-render', destroyUppyInstance);
document.addEventListener('DOMContentLoaded', initRecommendationFileUppyIfPresent);
document.addEventListener('turbolinks:load', initRecommendationFileUppyIfPresent);

// The pack tag executes before <body> exists (no defer), so document.body
// isn't available yet at top level — start observing once it is.
function startObservingBody() {
    if (!document.body) {
        setTimeout(startObservingBody, 0);
        return;
    }
    new MutationObserver(handleDomMutation).observe(document.body, {childList: true, subtree: true});
    initRecommendationFileUppyIfPresent();
}

startObservingBody();
